/**
 * Branded HTML templates for operational notification emails (OMN-296).
 *
 * Produces a shared, table-based, inline-styled layout consistent with the
 * invitation email (blue gradient header, "OmniFlyer." wordmark, #2563eb brand
 * blue) plus a plain-text fallback. Used by NotificationService to render the
 * htmlBody passed to the send-notification-email Edge Function.
 */

import { NotificationType } from './notificationService';

export interface NotificationEmailContent {
  type: NotificationType;
  /** Short pill label, e.g. "Squawk Reported". */
  badgeLabel: string;
  /** Main headline, e.g. "Squawk reported on N172SF". */
  headline: string;
  /** Detail rows shown as "Label: value". */
  details?: Array<{ label: string; value: string }>;
  /** Free-text paragraph(s) below the details. */
  bodyText?: string;
  /** Optional call-to-action. */
  ctaLabel?: string;
  ctaUrl?: string;
  /** Organization name for header + footer. */
  orgName?: string;
  /** Why the recipient is getting this, e.g. "an administrator". */
  recipientContext?: string;
}

const BRAND_BLUE = '#2563eb';
const BRAND_BLUE_DARK = '#1e40af';

// Badge accent colour by notification category.
const BADGE_COLORS: Record<NotificationType, string> = {
  squawk_reported: '#b45309',            // amber-700
  aircraft_grounded: '#b91c1c',          // red-700
  maintenance_status_changed: '#b45309', // amber-700
  booking_cancelled: '#b91c1c',          // red-700
  booking_series_cancelled: '#b91c1c',   // red-700
  booking_created: '#2563eb',            // blue-600
  booking_completed: '#15803d',          // green-700
  member_invited: '#15803d',             // green-700
  member_role_changed: '#2563eb',        // blue-600
  membership_request_approved: '#15803d',  // green-700
  membership_request_rejected: '#b91c1c',  // red-700
};

export function escapeHtml(input: string): string {
  return String(input ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Build the branded HTML + plain-text bodies for a notification email. */
export function renderNotificationEmail(c: NotificationEmailContent): { html: string; text: string } {
  const badgeColor = BADGE_COLORS[c.type] || BRAND_BLUE;
  const org = c.orgName ? escapeHtml(c.orgName) : 'your organization';

  const detailRows = (c.details || [])
    .filter(d => d.value != null && String(d.value).trim() !== '')
    .map(
      d => `
        <tr>
          <td style="padding:4px 0;color:#6b7280;font-size:14px;width:140px;vertical-align:top;">${escapeHtml(d.label)}</td>
          <td style="padding:4px 0;color:#111827;font-size:14px;font-weight:600;">${escapeHtml(d.value)}</td>
        </tr>`
    )
    .join('');

  const detailsBlock = detailRows
    ? `<table role="presentation" cellpadding="0" cellspacing="0" style="width:100%;margin:8px 0 4px 0;">${detailRows}</table>`
    : '';

  const bodyBlock = c.bodyText
    ? `<p style="margin:16px 0 0 0;color:#374151;font-size:15px;line-height:1.6;">${escapeHtml(c.bodyText)}</p>`
    : '';

  const ctaBlock =
    c.ctaLabel && c.ctaUrl
      ? `
        <table role="presentation" cellpadding="0" cellspacing="0" style="margin:28px 0 4px 0;">
          <tr>
            <td style="border-radius:8px;background:${BRAND_BLUE};">
              <a href="${escapeHtml(c.ctaUrl)}" target="_blank"
                 style="display:inline-block;padding:12px 28px;color:#ffffff;font-size:15px;font-weight:600;text-decoration:none;border-radius:8px;">
                ${escapeHtml(c.ctaLabel)}
              </a>
            </td>
          </tr>
        </table>`
      : '';

  const html = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;background:#f3f4f6;">
  <table role="presentation" cellpadding="0" cellspacing="0" style="width:100%;background:#f3f4f6;padding:24px 0;">
    <tr>
      <td align="center">
        <table role="presentation" cellpadding="0" cellspacing="0" style="width:600px;max-width:100%;background:#ffffff;border-radius:12px;overflow:hidden;font-family:Arial,Helvetica,sans-serif;">
          <tr>
            <td style="background:linear-gradient(135deg,${BRAND_BLUE} 0%,${BRAND_BLUE_DARK} 100%);padding:32px 40px;text-align:center;">
              <div style="font-size:26px;font-weight:700;color:#ffffff;letter-spacing:-0.5px;">OmniFlyer<span style="color:#ff2d20;">.</span></div>
              <div style="margin-top:6px;color:#dbeafe;font-size:13px;">${org}</div>
            </td>
          </tr>
          <tr>
            <td style="padding:36px 40px 40px 40px;">
              <span style="display:inline-block;background:${badgeColor};color:#ffffff;font-size:11px;font-weight:700;letter-spacing:0.5px;text-transform:uppercase;padding:5px 12px;border-radius:999px;">${escapeHtml(c.badgeLabel)}</span>
              <h1 style="margin:18px 0 0 0;color:#111827;font-size:22px;line-height:1.3;">${escapeHtml(c.headline)}</h1>
              ${detailsBlock}
              ${bodyBlock}
              ${ctaBlock}
            </td>
          </tr>
          <tr>
            <td style="padding:24px 40px;border-top:1px solid #e5e7eb;">
              <p style="margin:0;color:#9ca3af;font-size:12px;line-height:1.6;">
                You're receiving this because you are ${escapeHtml(c.recipientContext || 'a member')} at ${org}.
              </p>
              <p style="margin:8px 0 0 0;color:#9ca3af;font-size:12px;">OmniFlyer · Operational control for flying clubs and flight schools</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

  // Plain-text fallback.
  const textLines = [
    c.badgeLabel.toUpperCase(),
    '',
    c.headline,
    '',
    ...(c.details || [])
      .filter(d => d.value != null && String(d.value).trim() !== '')
      .map(d => `${d.label}: ${d.value}`),
  ];
  if (c.bodyText) textLines.push('', c.bodyText);
  if (c.ctaLabel && c.ctaUrl) textLines.push('', `${c.ctaLabel}: ${c.ctaUrl}`);
  textLines.push('', `— OmniFlyer · ${c.orgName || 'OmniFlyer'}`);
  const text = textLines.join('\n');

  return { html, text };
}
